/**
 * activity-types-store — interface com /api/v1/activity-types (R5.2 Bloco G).
 *
 * Tabela unificada (migration 024): activity / task / event / note.
 *
 * Hooks:
 *   - useActivityTypes({categoria?, workflow?, includeInactive?}) — lista
 *   - useActivityType(id) — single
 *   - useActivityTypeActions() — create/update/delete
 *
 * Cache: 5min staleTime (catálogo muda raramente).
 */

import { useCallback, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { api, hasToken } from './api';

export type ActivityCategoria = 'activity' | 'task' | 'event' | 'note';

export interface ActivityType {
  id: string;
  slug: string;
  label: string;
  categoria: ActivityCategoria;
  icone: string | null;
  cor: string | null;
  workflowSlug: string | null;
  sistema: boolean;
  ativo: boolean;
  ordem: number;
  defaultDuracaoMin: number | null;
  exigeDescricao: boolean;
  /**
   * M-1 (FEAT-V2-029 / HK-1): tipos com prazo. Quando true, o modal unificado
   * "+ Nova atividade" delega ao <CreateTaskDialog> (Calendar sync, conflito,
   * invitations). Quando false, é só registro no histórico do card (sem prazo).
   */
  requiresSchedule: boolean;
  criadoEmIso: string;
  atualizadoEmIso: string;
}

export interface CreateActivityTypeInput {
  slug: string;
  label: string;
  categoria: ActivityCategoria;
  icone?: string | null;
  cor?: string | null;
  workflowSlug?: string | null;
  ordem?: number;
  defaultDuracaoMin?: number | null;
  exigeDescricao?: boolean;
  /** M-1: opt-in pra schedule (Calendar sync). Default backend = false. */
  requiresSchedule?: boolean;
}

export type UpdateActivityTypeInput = Partial<
  Omit<CreateActivityTypeInput, 'slug' | 'categoria' | 'workflowSlug'>
> & { ativo?: boolean; requiresSchedule?: boolean };

export const ACTIVITY_TYPES_KEYS = {
  all: ['v1', 'activity-types'] as const,
  list: (filters: Record<string, unknown>) => ['v1', 'activity-types', { filters }] as const,
  byId: (id: string) => ['v1', 'activity-types', id] as const,
};

interface UseListOptions {
  categoria?: ActivityCategoria | null;
  workflow?: string | null;
  includeInactive?: boolean;
}

export function useActivityTypes(options: UseListOptions = {}) {
  const { categoria, workflow, includeInactive = false } = options;
  const params = new URLSearchParams();
  if (categoria) params.set('categoria', categoria);
  if (workflow) params.set('workflow', workflow);
  if (includeInactive) params.set('include_inactive', '1');
  const qs = params.toString();
  const url = qs ? `/api/v1/activity-types?${qs}` : '/api/v1/activity-types';
  return useQuery({
    queryKey: ACTIVITY_TYPES_KEYS.list({ categoria, workflow, includeInactive }),
    queryFn: () => api.get<ActivityType[]>(url),
    enabled: hasToken(),
    staleTime: 5 * 60_000,
  });
}

export function useActivityType(id: string | null) {
  return useQuery({
    queryKey: ACTIVITY_TYPES_KEYS.byId(id ?? ''),
    queryFn: () =>
      api.get<ActivityType>(`/api/v1/activity-types/${encodeURIComponent(id!)}`),
    enabled: !!id && hasToken(),
    staleTime: 5 * 60_000,
  });
}

export function useActivityTypeActions() {
  const qc = useQueryClient();
  const [pending, setPending] = useState<string | null>(null);

  const invalidate = () => qc.invalidateQueries({ queryKey: ACTIVITY_TYPES_KEYS.all });

  const create = useCallback(
    async (input: CreateActivityTypeInput): Promise<ActivityType> => {
      setPending('create');
      try {
        const t = await api.post<ActivityType>('/api/v1/activity-types', input);
        invalidate();
        return t;
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  const update = useCallback(
    async (id: string, input: UpdateActivityTypeInput): Promise<ActivityType> => {
      setPending(id);
      try {
        const t = await api.put<ActivityType>(
          `/api/v1/activity-types/${encodeURIComponent(id)}`,
          input
        );
        invalidate();
        return t;
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  const remove = useCallback(
    async (id: string): Promise<void> => {
      setPending(id);
      try {
        await api.delete(`/api/v1/activity-types/${encodeURIComponent(id)}`);
        invalidate();
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  // T.2 / U.2 (FEAT-V2-013): Visibility scopes
  const updateVisibility = useCallback(
    async (
      id: string,
      scopes: Array<{ scopeType: 'global' | 'workflow' | 'dept' | 'user'; scopeId?: string | null }>
    ): Promise<{ activityTypeId: string; scopes: typeof scopes }> => {
      setPending(`visibility-${id}`);
      try {
        const r = await api.put<{ activityTypeId: string; scopes: typeof scopes }>(
          `/api/v1/activity-types/${encodeURIComponent(id)}/visibility`,
          { scopes }
        );
        invalidate();
        return r;
      } finally {
        setPending(null);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [qc]
  );

  return { create, update, remove, updateVisibility, pending };
}

// ─── Visibility scopes (T.2 / FEAT-V2-013) ───────────────────────────────

export type VisibilityScopeType = 'global' | 'workflow' | 'dept' | 'user';

export interface VisibilityScope {
  id?: string;
  scopeType: VisibilityScopeType;
  scopeId: string | null;
}

export interface ActivityTypeVisibility {
  activityTypeId: string;
  scopes: VisibilityScope[];
}

/**
 * Carrega escopos de visibilidade de um activity_type.
 * Backend: GET /api/v1/activity-types/:id/visibility
 *
 * Retorna `scopes: []` quando o tipo é "global by default" (sem scopes
 * configurados ainda — migração de dados garante que tipos pré-existentes
 * ganham scope=global automático).
 */
export function useActivityTypeVisibility(id: string | null) {
  return useQuery({
    queryKey: ['v1', 'activity-types', id ?? '', 'visibility'],
    queryFn: () =>
      api.get<ActivityTypeVisibility>(
        `/api/v1/activity-types/${encodeURIComponent(id!)}/visibility`
      ),
    enabled: !!id && hasToken(),
    staleTime: 60_000,
  });
}
